import aqe from '@pushly/aqe'
import axios, { Canceler, AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios'
import { convertCase, shouldUseLegacyAuthHeader } from '../_utils/utils'
import { APPLICATION_VERSION } from '../constants'
import { ApiCallOptions } from '@pushly/aquarium-utils/lib/react'
import { notification } from 'antd'
import { Container } from 'typescript-ioc/es5'
import { AppState } from '../stores/app'
import { PushlyCookie } from '../enums/pushly-cookie.enum'
import { initUserActivityTracker } from '@pushly/aqe/lib/utils/session/user-activity-tracker'
import { initCrossTabSessionSync } from '@pushly/aqe/lib/utils/session/session-state-sync'
import { handleSessionResponse, applyUserActivityHeader } from '@pushly/aqe/lib/utils/session/session-interceptor'
import { installSessionDebug, isSessionDebugFrozen, isDebugFreezeSignal, applyDebugFreeze } from '@pushly/aqe/lib/utils/session/session-debug'

initUserActivityTracker()

// Cross-tab session sync: when another tab updates session state, apply it locally
initCrossTabSessionSync({
    getState: () => Container.get(AppState),
})

export function isLocalEnv() {
    return process.env.LOCAL_ENV?.toString() === 'true' || process.env.NODE_ENV === 'local'
}

export function platformRequestInterceptor(request: AxiosRequestConfig) {
    if (request.external === true) {
        return request
    }

    // Block all requests when session is expired — no point hitting the server
    try {
        const appState: AppState = Container.get(AppState)
        if (appState.sessionExpired) {
            return Promise.reject(new axios.Cancel('Session expired — request blocked'))
        }
    } catch {
        // Container not ready yet — allow the request
    }

    request.withCredentials = true
    request.headers['X-Application-Version'] = APPLICATION_VERSION
    request.headers['X-Application-Source'] = 'pufferfish'

    if (shouldUseLegacyAuthHeader()) {
        request.headers.Authorization = `Bearer ${window.localStorage.getItem(PushlyCookie.PLATFORM_COOKIE)}`
    }

    applyUserActivityHeader(request.headers, request, aqe.defaults.publicApiDomain, request.url)

    if (request.data && !request.skipCaseConversion) {
        request.data = convertCase(request.data, 'snake')
    }

    return request
}

export function platformResponseInterceptor(response: AxiosResponse) {
    const resData = response.data ?? {}

    if (resData.status === 'success' && !response.config.skipCaseConversion) {
        response.data.data = convertCase(response.data.data, 'camel')
    }

    // Update session state from API headers if present
    try {
        const appState: AppState = Container.get(AppState)
        handleSessionResponse(response, appState)
    } catch (error) {
        console.warn('Error updating session state from headers:', error)
    }

    return response
}

/**
 * Parses a string value as a positive integer.
 * Returns null for undefined, NaN, negative, zero, or non-finite values.
 */
/** Error types that indicate the session is dead and should trigger the expiration screen */
const SESSION_EXPIRED_ERROR_TYPES = /^(auth_token_expired|auth_token_missing|session_idle_timeout|auth_token_revoked)$/i

export function platformReponseErrorInterceptor(error: any) {
    const errorRes = error.response

    if (errorRes) {
        const resConfig: any = errorRes.config ?? {}
        const errorType: string = errorRes.data?.error_type ?? 'unknown'

        const appState: AppState = Container.get(AppState)

        // Session expiration handling runs regardless of bypassGlobalErrorHandlers
        if (errorRes.status === 401 && SESSION_EXPIRED_ERROR_TYPES.test(errorType)) {
            if (appState?.isAuthenticated && !appState.sessionExpired) {
                appState.sessionExpired = true
            }
        }

        if (!resConfig.bypassGlobalErrorHandlers) {
            if (errorRes.status === 429) {
                const errMsg = errorRes.data?.message ?? 'Please reduce your request rate.'
                const notifDescription = `Pushly was unable to complete your request. ${errMsg.replace(/\.$/, '')}.`

                notification.error({
                    key: 'rate-limit-exception',
                    className: 'simple-notification',
                    message: undefined,
                    description: notifDescription,
                    duration: 30,
                })
            }
        }
    }

    throw error
}

export const setupAxios = () => {
    axios.interceptors.request.use(platformRequestInterceptor)
    aqe.useAxiosInterceptor('request', platformRequestInterceptor)

    // pass through for success
    // implement global error handlers
    axios.interceptors.response.use(platformResponseInterceptor, platformReponseErrorInterceptor)
    aqe.useAxiosInterceptor('response', platformResponseInterceptor, platformReponseErrorInterceptor)

    // Dev-only: expose session debug helpers on window.__sessionDebug
    try {
        const appState: AppState = Container.get(AppState)
        // Enable in all environments (incl. deployed dev/staging, which build
        // with NODE_ENV=production). The helper only affects the current tab's
        // client-side session UX, so it's safe outside local dev.
        installSessionDebug(appState, { enabled: true })
    } catch {}
}

export const axiosCancellationRequests: Record<string, Canceler> = {}
export const isAxiosCancellation = (error: any) => axios.isCancel(error)
export const isAxiosResponseError = (error: Error): error is AxiosError => !!(error as AxiosError).response

export type AxiosMethod = 'get' | 'put' | 'post' | 'patch' | 'delete'
interface IAxiosRequest {
    url: string
    data?: any
    config?: (AxiosRequestConfig & { external?: boolean }) | undefined
}

export async function axiosFetch(method: AxiosMethod, pack: IAxiosRequest, cancellationKey?: string): Promise<any> {
    pack.config = pack.config || {}
    const args: any[] = [pack.url]
    if (pack.data) args.push(pack.data)

    if (cancellationKey) {
        pack.config.cancelToken = await buildCancellationRequest(cancellationKey)
    }

    args.push(pack.config)
    const response = await axios[method.toLowerCase()](...args)

    if (cancellationKey && axiosCancellationRequests[cancellationKey]) {
        delete axiosCancellationRequests[cancellationKey]
    }

    return response
}

const buildCancellationRequest = async (cancellationKey: string): Promise<any> => {
    const handler = axiosCancellationRequests[cancellationKey]

    if (handler) {
        await handler()
        delete axiosCancellationRequests[cancellationKey]
    }

    return new axios.CancelToken((cb) => {
        axiosCancellationRequests[cancellationKey] = cb
    })
}

export const convertCancellationKeyToToken = async (opts?: ApiCallOptions): Promise<void> => {
    if (!opts?.cancelToken && !!opts?.cancellationKey) {
        opts.cancelToken = await buildCancellationRequest(opts.cancellationKey)
        delete opts.cancellationKey
    }
}

export const cancelNamedAxiosRequest = (key: string) => {
    if (key in axiosCancellationRequests) {
        axiosCancellationRequests[key]()
    }
}
